import { orderService, OrderResponse } from './OrderService';
import { Callout } from '../models/callout';

/** Repair / work type for a labor estimate */
export type RepairType = 'repair' | 'replace' | 'paint' | 'diagnose' | 'other';

/** Labor / repair estimate attached to a cart item's part_data */
export interface RepairEstimate {
  labor_hours: number;
  labor_rate?: number;
  labor_note?: string;
  repair_type?: RepairType;
}

export interface CartItem {
  id?: string;
  part_id: string;
  structured_data: {
    vin_data: {
      make: string;
      manufacturer: string;
      model: string;
      trim: string;
      vin: string;
      year: string;
    };
    part_data: {
      part_id: string;
      part_number: string;
      description: string;
      price: string;
      not_found?: boolean;
      is_service?: boolean;
      labor_estimate?: RepairEstimate;
      [key: string]: any;
    };
    selection_reason?: string;
  };
  image?: string;
  imageCallout?: Callout[];
  quantity: number;
  timestamp: Date;
  related_user_query?: string | null;
  is_service?: boolean;
  translated_description?: string | null;
}

export interface AddItemResult {
  success: boolean;
  message: string;
  updatedItems: CartItem[];
  addedToOrder: boolean;
  /** The full updated order returned by the PUT — avoids a redundant GET after add. */
  updatedOrder?: OrderResponse;
}

/**
 * Central service for managing cart items per order
 * Handles adding, removing, and updating items in both cart and active orders
 */
export class CartOrderService {
  private static instance: CartOrderService;

  /**
   * Per-order mutex: ensures concurrent addItem calls for the same order are
   * serialized so no two PUTs race against each other with stale item lists.
   */
  private orderLocks = new Map<number, Promise<any>>();

  public static getInstance(): CartOrderService {
    if (!CartOrderService.instance) {
      CartOrderService.instance = new CartOrderService();
    }
    return CartOrderService.instance;
  }

  /**
   * Serialize an async operation against a specific order ID.
   * The next call for the same orderId won't start until the current one resolves.
   */
  private withOrderLock<T>(orderId: number, fn: () => Promise<T>): Promise<T> {
    const prev = this.orderLocks.get(orderId) ?? Promise.resolve();
    const next = prev.then(() => fn(), () => fn()); // run even if previous failed
    this.orderLocks.set(orderId, next.catch(() => {})); // keep chain alive
    return next;
  }

  /**
   * Add an item to either the active order or cart
   * @param item - The item to add
   * @param activeOrderId - The ID of the active order, if any
   * @param currentCartItems - Current items in the cart
   * @param currentOrderItems - Ignored (kept for API compatibility). The service
   *   now always fetches fresh state inside the lock to prevent race conditions
   *   when multiple items are added in rapid succession.
   * @returns Result containing success status and updated items
   */
  public async addItem(
    item: Omit<CartItem, 'quantity' | 'timestamp'>,
    activeOrderId: number | null,
    currentCartItems: CartItem[] = [],
    _currentOrderItems?: CartItem[],
    /** Initial quantity for a NEW line (defaults to 1). Used to seed the cart from
     *  the catalog-recommended quantity. Ignored when merging into an existing line. */
    quantity: number = 1
  ): Promise<AddItemResult> {
    // If there's an active order, serialize the add through the per-order lock
    if (activeOrderId) {
      try {
        const result = await this.withOrderLock(activeOrderId, () =>
          // Always fetch fresh items inside the lock — avoids stale-read races
          this.addItemToOrder(item, activeOrderId, undefined, quantity)
        );
        return result;
      } catch (error) {
        console.error('Failed to add item to order, falling back to cart:', error);
        // Fall through to add to cart
      }
    }

    // Add to cart if no active order or if adding to order failed
    const updatedItems = this.addItemToCart(item, currentCartItems, quantity);
    return {
      success: true,
      message: 'Item added to cart',
      updatedItems,
      addedToOrder: false
    };
  }

  /**
   * Add an item to a specific order
   * @param item - The item to add
   * @param orderId - The order ID
   * @param existingOrderItems - Current order items from memory; if omitted, a GET is issued to fetch them
   * @returns Result containing success status and updated items
   */
  private async addItemToOrder(
    item: Omit<CartItem, 'quantity' | 'timestamp'>,
    orderId: number,
    existingOrderItems?: CartItem[],
    quantity: number = 1
  ): Promise<AddItemResult> {
    // Use supplied items when available — skips one GET round-trip
    let existingItems: CartItem[];
    if (existingOrderItems !== undefined) {
      existingItems = existingOrderItems;
    } else {
      const order = await orderService.getOrderById(orderId);
      existingItems = order.parts_in_order?.items || [];
    }

    const existingIndex = existingItems.findIndex(
      (existingItem) => existingItem.structured_data?.part_data?.part_id === item.structured_data.part_data.part_id
    );

    let mergedItems: CartItem[];
    if (existingIndex >= 0) {
      // Update quantity if item exists
      mergedItems = [...existingItems];
      mergedItems[existingIndex] = {
        ...mergedItems[existingIndex],
        quantity: mergedItems[existingIndex].quantity + 1
      };
    } else {
      // Add new item seeded with the catalog-recommended quantity and generate ID
      const newItem: CartItem = {
        ...item,
        id: `order_${orderId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        quantity: Math.max(1, Math.floor(quantity)),
        timestamp: new Date()
      };
      mergedItems = [...existingItems, newItem];
    }

    // Calculate totals
    const uniqueVins = Array.from(
      new Set(mergedItems.map((orderItem) => orderItem.structured_data.vin_data.vin))
    );
    const totalItems = mergedItems.reduce((sum, orderItem) => sum + orderItem.quantity, 0);

    // Ensure all items have IDs
    const itemsWithIds = mergedItems.map((orderItem) => ({
      ...orderItem,
      id: orderItem.id || `order_${orderId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }));

    // Update the order — the PUT response already contains the full updated order,
    // so callers don't need to issue a second GET after this call.
    const updateData = {
      parts_in_order: {
        items: itemsWithIds as any,
        total_items: totalItems,
        export_timestamp: new Date().toISOString(),
        vins: uniqueVins
      }
    };

    const updatedOrder = await orderService.updateOrder(orderId, updateData);

    return {
      success: true,
      message: 'Item added to order',
      updatedItems: mergedItems,
      addedToOrder: true,
      updatedOrder,
    };
  }

  /**
   * Add an item to the cart (local state)
   * @param item - The item to add
   * @param currentCartItems - Current items in the cart
   * @returns Updated cart items array
   */
  private addItemToCart(
    item: Omit<CartItem, 'quantity' | 'timestamp'>,
    currentCartItems: CartItem[],
    quantity: number = 1
  ): CartItem[] {
    const existingItemIndex = currentCartItems.findIndex(
      (cartItem) => cartItem.part_id === item.part_id
    );

    if (existingItemIndex >= 0) {
      // Update quantity of existing item
      const updatedItems = [...currentCartItems];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + 1
      };
      return updatedItems;
    } else {
      // Add new item seeded with the catalog-recommended quantity
      const newItem: CartItem = {
        ...item,
        quantity: Math.max(1, Math.floor(quantity)),
        timestamp: new Date()
      };
      return [...currentCartItems, newItem];
    }
  }

  /**
   * Remove an item from an order
   * @param partId - The part ID to remove
   * @param orderId - The order ID
   * @returns Updated order response
   */
  public async removeItemFromOrder(partId: string, orderId: number): Promise<OrderResponse> {
    const order = await orderService.getOrderById(orderId);
    const existingItems: CartItem[] = order.parts_in_order?.items || [];
    
    const updatedItems = existingItems.filter(
      (item) => item.structured_data?.part_data?.part_id !== partId
    );

    const uniqueVins = Array.from(
      new Set(updatedItems.map((item) => item.structured_data.vin_data.vin))
    );
    const totalItems = updatedItems.reduce((sum, item) => sum + item.quantity, 0);

    // Ensure all items have IDs
    const itemsWithIds = updatedItems.map((item) => ({
      ...item,
      id: item.id || `order_${orderId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }));

    const updateData = {
      parts_in_order: {
        items: itemsWithIds as any,
        total_items: totalItems,
        export_timestamp: new Date().toISOString(),
        vins: uniqueVins
      }
    };

    const updatedOrder = await orderService.updateOrder(orderId, updateData);
    return updatedOrder;
  }

  /**
   * Update item quantity in an order
   * @param partId - The part ID to update
   * @param quantity - New quantity
   * @param orderId - The order ID
   * @returns Updated order response
   */
  public async updateItemQuantityInOrder(
    partId: string,
    quantity: number,
    orderId: number
  ): Promise<OrderResponse> {
    const order = await orderService.getOrderById(orderId);
    const existingItems: CartItem[] = order.parts_in_order?.items || [];
    
    const updatedItems = existingItems.map((item) =>
      item.structured_data?.part_data?.part_id === partId
        ? { ...item, quantity }
        : item
    );

    const uniqueVins = Array.from(
      new Set(updatedItems.map((item) => item.structured_data.vin_data.vin))
    );
    const totalItems = updatedItems.reduce((sum, item) => sum + item.quantity, 0);

    // Ensure all items have IDs
    const itemsWithIds = updatedItems.map((item) => ({
      ...item,
      id: item.id || `order_${orderId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }));

    const updateData = {
      parts_in_order: {
        items: itemsWithIds as any,
        total_items: totalItems,
        export_timestamp: new Date().toISOString(),
        vins: uniqueVins
      }
    };

    const updatedOrder = await orderService.updateOrder(orderId, updateData);
    return updatedOrder;
  }

  /**
   * Get items from an order
   * @param orderId - The order ID
   * @returns Array of cart items
   */
  public async getOrderItems(orderId: number): Promise<CartItem[]> {
    try {
      const order = await orderService.getOrderById(orderId);
      return order.parts_in_order?.items || [];
    } catch (error) {
      console.error('Failed to get order items:', error);
      return [];
    }
  }

  /**
   * Check if a part exists in cart or order
   * @param partId - The part ID to check
   * @param cartItems - Current cart items
   * @param orderItems - Current order items
   * @returns Boolean indicating if part exists
   */
  public isPartInCartOrOrder(
    partId: string,
    cartItems: CartItem[] = [],
    orderItems: CartItem[] = []
  ): boolean {
    const inCart = cartItems.some((item) => item.part_id === partId);
    const inOrder = orderItems.some(
      (item) => item.structured_data?.part_data?.part_id === partId
    );
    return inCart || inOrder;
  }

  /**
   * Move all cart items to an order
   * @param cartItems - Items to move
   * @param orderId - Target order ID
   * @returns Updated order items
   */
  public async moveCartItemsToOrder(
    cartItems: CartItem[],
    orderId: number
  ): Promise<CartItem[]> {
    const order = await orderService.getOrderById(orderId);
    const existingItems: CartItem[] = order.parts_in_order?.items || [];

    // Merge cart items with existing order items
    const itemsMap = new Map<string, CartItem>();

    // Add existing items to map
    existingItems.forEach((item) => {
      itemsMap.set(item.structured_data.part_data.part_id, item);
    });

    // Merge or add cart items
    cartItems.forEach((cartItem) => {
      const partId = cartItem.structured_data.part_data.part_id;
      const existing = itemsMap.get(partId);
      
      if (existing) {
        // Update quantity
        itemsMap.set(partId, {
          ...existing,
          quantity: existing.quantity + cartItem.quantity
        });
      } else {
        // Add new item
        itemsMap.set(partId, cartItem);
      }
    });

    const mergedItems = Array.from(itemsMap.values());

    const uniqueVins = Array.from(
      new Set(mergedItems.map((item) => item.structured_data.vin_data.vin))
    );
    const totalItems = mergedItems.reduce((sum, item) => sum + item.quantity, 0);

    // Ensure all items have IDs
    const itemsWithIds = mergedItems.map((item) => ({
      ...item,
      id: item.id || `order_${orderId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }));

    const updateData = {
      parts_in_order: {
        items: itemsWithIds as any,
        total_items: totalItems,
        export_timestamp: new Date().toISOString(),
        vins: uniqueVins
      }
    };

    await orderService.updateOrder(orderId, updateData);
    return mergedItems;
  }

  /**
   * Clear all items from an order
   * @param orderId - The order ID
   */
  public async clearOrderItems(orderId: number): Promise<void> {
    const updateData = {
      parts_in_order: {
        items: [],
        total_items: 0,
        export_timestamp: new Date().toISOString(),
        vins: []
      }
    };

    await orderService.updateOrder(orderId, updateData);
  }
}

// Export singleton instance
export const cartOrderService = CartOrderService.getInstance();
